import { z } from 'zod';
import { StorageObjectKeySchema } from '../storage.js';
import {
  LogisticsCalculationPaymentLabelSchema,
  LogisticsCalculationStyleSchema,
} from './status.js';

export const LOGISTICS_CALCULATION_MAX_CARGOS = 100;
export const LOGISTICS_CALCULATION_MAX_LINES = 200;
export const LOGISTICS_CALCULATION_MAX_EXTRA_COSTS = 3;
export const LOGISTICS_CALCULATION_TEXT_MAX = 500;
export const LOGISTICS_CALCULATION_NOTE_MAX = 4_000;
export const LOGISTICS_CALCULATION_DECIMAL_MAX = 40;

export const LogisticsCalculationCurrencySchema = z.enum(['USD', 'CNY', 'RUB']);
export type LogisticsCalculationCurrency = z.infer<typeof LogisticsCalculationCurrencySchema>;
/** Frozen customer-layout marker; absence preserves the legacy v1 document. */
export const LogisticsCalculationLayoutVersionSchema = z.literal('v2');
export type LogisticsCalculationLayoutVersion = z.infer<
  typeof LogisticsCalculationLayoutVersionSchema
>;
export const LogisticsCalculationSectionSchema = z.enum([
  'chinaLogistics',
  'chinaExpenses',
  'rfExpenses',
  'rfLogistics',
]);
export type LogisticsCalculationSection = z.infer<typeof LogisticsCalculationSectionSchema>;
export const LOGISTICS_CALCULATION_SECTIONS = LogisticsCalculationSectionSchema.options;
/** Which measure the manager priced a cargo by; absence reads as by weight. */
export const LogisticsCalculationPricingBasisSchema = z.enum(['weight', 'volume']);
export type LogisticsCalculationPricingBasis = z.infer<
  typeof LogisticsCalculationPricingBasisSchema
>;
export const LogisticsCalculationUnitSchema = z.enum([
  'kg',
  'm3',
  'package',
  'item',
  'cargo',
  'trip',
  'day',
  'fixed',
  'custom',
]);
export type LogisticsCalculationUnit = z.infer<typeof LogisticsCalculationUnitSchema>;

export const LogisticsCalculationCargoExpenseFieldSchema = z.enum([
  'chinaPayment',
  'chinaWarehouseDispatch',
  'chinaWarehouseInspection',
  'chinaWarehousePackaging',
  'transportationTariff',
  'transportationTariffForClients',
  'insuranceCost',
  'packagingCost',
  'addressTransportationCost',
  'prr',
  'tatCost',
]);
export type LogisticsCalculationCargoExpenseField = z.infer<
  typeof LogisticsCalculationCargoExpenseFieldSchema
>;
export const LOGISTICS_CALCULATION_CARGO_EXPENSE_FIELDS =
  LogisticsCalculationCargoExpenseFieldSchema.options;

/** CARGO source values stay uncapped because their registry columns are unbounded. */
export const LogisticsCalculationCargoLineSourceMetadataSchema = z
  .object({ field: LogisticsCalculationCargoExpenseFieldSchema, value: z.string() })
  .strict();
export type LogisticsCalculationCargoLineSourceMetadata = z.infer<
  typeof LogisticsCalculationCargoLineSourceMetadataSchema
>;

const CargoExpenseSnapshotShape = {
  chinaPayment: z.string().optional(),
  chinaWarehouseDispatch: z.string().optional(),
  chinaWarehouseInspection: z.string().optional(),
  chinaWarehousePackaging: z.string().optional(),
  transportationTariff: z.string().optional(),
  transportationTariffForClients: z.string().optional(),
  insuranceCost: z.string().optional(),
  packagingCost: z.string().optional(),
  addressTransportationCost: z.string().optional(),
  prr: z.string().optional(),
  tatCost: z.string().optional(),
} satisfies Record<LogisticsCalculationCargoExpenseField, z.ZodOptional<z.ZodString>>;

/** Optional candidate source snapshot; each present value is copied verbatim. */
export const LogisticsCargoExpenseSnapshotSchema = z.object(CargoExpenseSnapshotShape).strict();
export type LogisticsCargoExpenseSnapshot = z.infer<typeof LogisticsCargoExpenseSnapshotSchema>;

const CappedText = z.string().max(LOGISTICS_CALCULATION_TEXT_MAX);
const NullableDisplayText = CappedText.nullable();
const DecimalInputSchema = z
  .string()
  .max(LOGISTICS_CALCULATION_DECIMAL_MAX)
  .regex(/^$|^(?:0|[1-9]\d*)(?:\.\d+)?$/, 'must be empty or a non-negative decimal');
const PositiveDecimalInputSchema = z
  .string()
  .max(LOGISTICS_CALCULATION_DECIMAL_MAX)
  .regex(/^(?:0*[1-9]\d*)(?:\.\d+)?$|^0*\.\d*[1-9]\d*$/, 'must be a positive decimal');

/** Positive document rate with at most five digits total and four fractional digits. */
export const LogisticsCalculationRateInputSchema = PositiveDecimalInputSchema.max(6).refine(
  (value) => {
    const [whole = '', fraction = ''] = value.split('.');
    return whole.length + fraction.length <= 5 && fraction.length <= 4;
  },
  'must have at most five digits total and four fraction digits',
);
// WHY two fraction digits and not `DecimalInputSchema`: a cost amount is typed
// by a manager and printed verbatim, so a third digit would print a cell the
// document total cannot reconcile with. A `rate` stays uncapped because its
// amount is always a rounded `money()` product.
const CostAmountInputSchema = z
  .string()
  .max(LOGISTICS_CALCULATION_DECIMAL_MAX)
  .regex(
    /^$|^(?:0|[1-9]\d*)(?:\.\d{1,2})?$/,
    'must be empty or a non-negative amount with at most two fraction digits',
  );

const CostShape = {
  amount: CostAmountInputSchema,
  currency: LogisticsCalculationCurrencySchema,
} as const;

/** A cost cell the manager types as an amount; an empty amount prints as an em dash. */
export const LogisticsCalculationCostSchema = z.object(CostShape).strict();
export type LogisticsCalculationCost = z.infer<typeof LogisticsCalculationCostSchema>;

/** The cost cells whose column prints a caption line under the amount. */
export const LogisticsCalculationCaptionedCostSchema = z
  .object({ ...CostShape, caption: CappedText.optional() })
  .strict();
export type LogisticsCalculationCaptionedCost = z.infer<
  typeof LogisticsCalculationCaptionedCostSchema
>;

const LineValuesShape = {
  serviceName: z.string().min(1).max(LOGISTICS_CALCULATION_TEXT_MAX),
  quantity: DecimalInputSchema,
  unit: LogisticsCalculationUnitSchema,
  customUnit: CappedText.optional(),
  rate: DecimalInputSchema,
  currency: LogisticsCalculationCurrencySchema,
  convertToUsd: z.boolean(),
  internalCost: DecimalInputSchema.optional(),
} as const;

function validateLine(
  value: {
    unit?: string | null | undefined;
    customUnit?: string | undefined;
    currency?: string | null | undefined;
    convertToUsd: boolean;
  },
  ctx: z.RefinementCtx,
): void {
  if (value.convertToUsd && value.currency !== 'CNY') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['convertToUsd'],
      message: 'convertToUsd is valid only for CNY lines',
    });
  }
  if (value.unit === 'custom' && !value.customUnit?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['customUnit'],
      message: 'customUnit is required for custom unit',
    });
  }
}

export const LogisticsCalculationCargoLineSchema = z
  .object({
    section: LogisticsCalculationSectionSchema,
    ...LineValuesShape,
    unit: LogisticsCalculationUnitSchema.nullish(),
    currency: LogisticsCalculationCurrencySchema.nullish(),
    sourceMetadata: LogisticsCalculationCargoLineSourceMetadataSchema.optional(),
  })
  .strict()
  .superRefine(validateLine);
export type LogisticsCalculationCargoLine = z.infer<typeof LogisticsCalculationCargoLineSchema>;

export const LogisticsCalculationCommonServiceLineSchema = z
  .object(LineValuesShape)
  .strict()
  .superRefine(validateLine);
export type LogisticsCalculationCommonServiceLine = z.infer<
  typeof LogisticsCalculationCommonServiceLineSchema
>;

export const LogisticsCalculationCargoContentSchema = z
  .object({
    sourceCargoArticleId: z.string().uuid().optional(),
    article: CappedText,
    name: NullableDisplayText,
    seats: NullableDisplayText,
    weight: NullableDisplayText,
    volume: NullableDisplayText,
    pricingBasis: LogisticsCalculationPricingBasisSchema.optional(),
    transportType: CappedText.optional(),
    departedAt: z.string().datetime().optional(),
    arrivedAt: z.string().datetime().optional(),
    photoObjectKey: StorageObjectKeySchema.optional(),
    // INVARIANT: every cost key stays optional with no `.default()`. A default
    // would let an ordinary parse of a legacy draft store the new shape while
    // no editor originates it, which a rollback could no longer read.
    transport: LogisticsCalculationCaptionedCostSchema.optional(),
    insurance: LogisticsCalculationCaptionedCostSchema.optional(),
    packaging: LogisticsCalculationCostSchema.optional(),
    extras: z
      .array(LogisticsCalculationCaptionedCostSchema)
      .max(LOGISTICS_CALCULATION_MAX_EXTRA_COSTS)
      .optional(),
    prr: LogisticsCalculationCostSchema.optional(),
    clientDelivery: LogisticsCalculationCostSchema.optional(),
    lines: z.array(LogisticsCalculationCargoLineSchema).max(LOGISTICS_CALCULATION_MAX_LINES),
  })
  .strict();
export type LogisticsCalculationCargoContent = z.infer<
  typeof LogisticsCalculationCargoContentSchema
>;

const ContentShape = {
  style: LogisticsCalculationStyleSchema.default('form'),
  clientCompany: CappedText,
  clientContactName: CappedText,
  clientContactPhone: z.string().max(64),
  documentDate: z.string().date(),
  layoutVersion: LogisticsCalculationLayoutVersionSchema.optional(),
  payDueDate: z.string().date(),
  managerName: CappedText,
  managerPhone: z.string().max(64),
  managerTelegram: CappedText.optional(),
  managerNote: z.string().max(LOGISTICS_CALCULATION_NOTE_MAX).optional(),
  paymentLabel: LogisticsCalculationPaymentLabelSchema,
  paymentDivisor: PositiveDecimalInputSchema.optional(),
  cnyPerUsdRate: PositiveDecimalInputSchema.optional(),
  rubPerUsdRate: PositiveDecimalInputSchema,
  rubPerCnyRate: LogisticsCalculationRateInputSchema.optional(),
  payableCurrency: z.enum(['RUB', 'USD', 'USD_RUB']).optional(),
  showAllRubReference: z.boolean().default(false),
  cargos: z.array(LogisticsCalculationCargoContentSchema).max(LOGISTICS_CALCULATION_MAX_CARGOS),
  commonServices: z
    .array(LogisticsCalculationCommonServiceLineSchema)
    .max(LOGISTICS_CALCULATION_MAX_LINES),
} as const;

/**
 * Rejects the one payment mode that may not carry a divisor.
 *
 * WHY exported instead of attached to the editable schema: writes are validated
 * against `LogisticsCalculationEditableContentSchema` only, and refining it
 * would turn it into a `ZodEffects`, which has no `.strip()` — the web form
 * round-trips stored content through exactly that call. The rule is hung on the
 * persisted schema and on the create/patch inputs instead, so no write path can
 * store the forbidden pair and no read can throw on what a write accepted.
 */
export function validatePaymentDivisor(
  content: { paymentLabel: string; paymentDivisor?: string | undefined },
  ctx: z.RefinementCtx,
  path: ReadonlyArray<string | number> = [],
): void {
  if (content.paymentLabel === 'individual' && content.paymentDivisor !== undefined) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...path, 'paymentDivisor'],
      message: 'paymentDivisor is not allowed for an individual payment',
    });
  }
}

/** @aiPii carries customer phone and owner-issued object-storage keys. */
export const LogisticsCalculationContentSchema = z
  .object(ContentShape)
  .strict()
  .superRefine((content, ctx) => validatePaymentDivisor(content, ctx));
export type LogisticsCalculationContent = z.infer<typeof LogisticsCalculationContentSchema>;

function requirePublishReadyLine(
  line: {
    quantity: string;
    rate: string;
    unit?: string | null | undefined;
    currency?: string | null | undefined;
  },
  path: ReadonlyArray<string | number>,
  ctx: z.RefinementCtx,
): void {
  for (const field of ['quantity', 'rate', 'unit', 'currency'] as const) {
    if (line[field] === '' || line[field] === null || line[field] === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...path, field],
        message: `${field} is required for publication`,
      });
    }
  }
}

/** Complete content accepted at the publication boundary; draft schemas remain intentionally lax. */
export const LogisticsCalculationPublishReadyContentSchema =
  LogisticsCalculationContentSchema.superRefine((content, ctx) => {
    if (content.cargos.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['cargos'],
        message: 'at least one cargo is required for publication',
      });
    }
    content.cargos.forEach((cargo, cargoIndex) => {
      cargo.lines.forEach((line, lineIndex) => {
        requirePublishReadyLine(line, ['cargos', cargoIndex, 'lines', lineIndex], ctx);
      });
    });
    content.commonServices.forEach((line, lineIndex) => {
      requirePublishReadyLine(line, ['commonServices', lineIndex], ctx);
    });
  });
export type LogisticsCalculationPublishReadyContent = z.infer<
  typeof LogisticsCalculationPublishReadyContentSchema
>;

/**
 * Caller-supplied draft content; server-owned dates and layout markers are
 * excluded.
 *
 * WHY no minimum cargo count: removing the last row and saving is how a manager
 * releases a cargo a mistaken draft holds. Publication still requires one —
 * `LogisticsCalculationPublishReadyContentSchema` owns that rule.
 */
export const LogisticsCalculationEditableContentSchema = z
  .object(ContentShape)
  .omit({ documentDate: true, layoutVersion: true })
  .strict();
export type LogisticsCalculationEditableContent = z.infer<
  typeof LogisticsCalculationEditableContentSchema
>;
